/**
 * Phase 3: Fork Validation
 *
 * Read-only validation of playground forks using GitHub's public API.
 * Fails closed on any error or missing required files.
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
  repo?: {
    owner: string;
    name: string;
    url: string;
  };
}

export interface ScenarioFile {
  path: string;
  exists: boolean;
  sha?: string;
}

// Required files for a valid playground fork
const REQUIRED_ROOT_FILES = [
  'README.md',
  'SCENARIOS.md',
  'package.json'
];

// Required scenario files
const REQUIRED_SCENARIO_FILES = [
  'scenarios/react-dashboard-null-crash/xable-scenario.json',
  'scenarios/api-validation-missing/xable-scenario.json',
  'scenarios/async-state-race/xable-scenario.json',
  'scenarios/full-stack-stabilization/xable-scenario.json',
  'scenarios/governed-workflow-simulation/xable-scenario.json',
  'scenarios/korros-governed-agent-challenge/xable-scenario.json'
];

/**
 * Validate a playground fork URL and check required files.
 *
 * Uses GitHub's public API (no authentication required for public repos).
 * Only validates structure - does not read file contents in Phase 3.
 */
export async function validatePlaygroundFork(repoUrl: string): Promise<ValidationResult> {
  try {
    // Parse GitHub URL
    const urlPattern = /^https:\/\/github\.com\/([^\/]+)\/([^\/]+)\/?$/;
    const match = repoUrl.match(urlPattern);

    if (!match) {
      return { valid: false, error: 'Please connect a fork of korzent/xable-playground.' };
    }

    const owner = match[1];
    const repo = match[2];

    // Validate repo name
    if (repo !== 'xable-playground') {
      return { valid: false, error: 'Please connect a fork of korzent/xable-playground.' };
    }

    // Check if repo exists and is public using GitHub API
    const repoResponse = await fetch(`https://api.github.com/repos/${owner}/${repo}`, {
      headers: {
        'Accept': 'application/vnd.github.v3+json'
      }
    });

    if (!repoResponse.ok) {
      if (repoResponse.status === 404) {
        return { valid: false, error: 'Repository not found. Please check the URL and try again.' };
      }
      if (repoResponse.status === 403) {
        return { valid: false, error: 'Repository is private. Only public forks are supported.' };
      }
      if (repoResponse.status === 401) {
        return { valid: false, error: 'Authentication required. This should not happen for public repos.' };
      }
      // GitHub rate limit or other errors
      return { valid: false, error: 'Could not access the repository. Please try again later.' };
    }

    const repoData = await repoResponse.json();

    // Verify it's public
    if (repoData.private) {
      return { valid: false, error: 'Repository is private. Only public forks are supported.' };
    }

    // Check required root files exist
    for (const file of REQUIRED_ROOT_FILES) {
      const fileResponse = await fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${file}`, {
        headers: {
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (!fileResponse.ok) {
        if (fileResponse.status === 404) {
          return { valid: false, error: `Missing required file: ${file}. This fork may not be a complete playground.` };
        }
        return { valid: false, error: `Could not read file: ${file}. Please try again.` };
      }
    }

    // Check required scenario files exist
    for (const file of REQUIRED_SCENARIO_FILES) {
      const fileResponse = await fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${file}`, {
        headers: {
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (!fileResponse.ok) {
        if (fileResponse.status === 404) {
          return { valid: false, error: `Missing scenario file: ${file}. This fork may not have all scenarios.` };
        }
        return { valid: false, error: `Could not read scenario file: ${file}. Please try again.` };
      }
    }

    // All validations passed
    return {
      valid: true,
      repo: {
        owner,
        name: repo,
        url: repoUrl
      }
    };
  } catch (error) {
    // Fail closed on any network error
    console.error('[Fork Validation] Error:', error);
    return { valid: false, error: 'Failed to validate fork. Please check your connection and try again.' };
  }
}

/**
 * Get list of available scenarios from a validated fork.
 */
export async function getAvailableScenarios(owner: string): Promise<string[]> {
  return [
    'React Dashboard Null Crash',
    'API Validation Missing',
    'Async State Race',
    'Full-Stack Stabilization',
    'Governed Workflow Simulation',
    'Korros Governed Agent Challenge'
  ];
}

/**
 * Map scenario name to file path.
 */
export function scenarioToPath(scenarioName: string): string {
  const mapping: Record<string, string> = {
    'React Dashboard Null Crash': 'scenarios/react-dashboard-null-crash',
    'API Validation Missing': 'scenarios/api-validation-missing',
    'Async State Race': 'scenarios/async-state-race',
    'Full-Stack Stabilization': 'scenarios/full-stack-stabilization',
    'Governed Workflow Simulation': 'scenarios/governed-workflow-simulation',
    'Korros Governed Agent Challenge': 'scenarios/korros-governed-agent-challenge'
  };

  return mapping[scenarioName] || '';
}

/**
 * Allowlisted files per scenario.
 * Only these files will be read - no repo crawling.
 */
const SCENARIO_ALLOWLIST: Record<string, string[]> = {
  'React Dashboard Null Crash': [
    'scenarios/react-dashboard-null-crash/xable-scenario.json',
    'scenarios/react-dashboard-null-crash/src/Dashboard.jsx',
    'scenarios/react-dashboard-null-crash/src/Dashboard.test.js'
  ],
  'API Validation Missing': [
    'scenarios/api-validation-missing/xable-scenario.json',
    'scenarios/api-validation-missing/src/api/routes/index.ts'
  ],
  'Async State Race': [
    'scenarios/async-state-race/xable-scenario.json',
    'scenarios/async-state-race/src/components/AsyncComponent.tsx'
  ],
  'Full-Stack Stabilization': [
    'scenarios/full-stack-stabilization/xable-scenario.json',
    'scenarios/full-stack-stabilization/src/server/index.ts',
    'scenarios/full-stack-stabilization/src/client/App.tsx'
  ],
  'Governed Workflow Simulation': [
    'scenarios/governed-workflow-simulation/xable-scenario.json',
    'scenarios/governed-workflow-simulation/src/workflow/executor.ts'
  ],
  'Korros Governed Agent Challenge': [
    'scenarios/korros-governed-agent-challenge/xable-scenario.json',
    'scenarios/korros-governed-agent-challenge/src/agent/controller.ts'
  ]
};

export interface ScenarioData {
  repoUrl: string;
  scenarioId: string;
  files: {
    path: string;
    hash: string;
    summary: string;
  }[];
  timestamp: string;
}

/**
 * Read allowlisted files for a scenario from a fork.
 *
 * Only reads files in the allowlist - does not crawl the repo.
 * Returns hashes and short summaries - does not store raw code permanently.
 */
export async function readScenarioFiles(
  owner: string,
  scenarioName: string
): Promise<{ success: boolean; data?: ScenarioData; error?: string }> {
  try {
    const scenarioPath = scenarioToPath(scenarioName);
    if (!scenarioPath) {
      return { success: false, error: 'Unsupported scenario.' };
    }

    const allowlist = SCENARIO_ALLOWLIST[scenarioName];
    if (!allowlist) {
      return { success: false, error: 'Scenario not found.' };
    }

    const files: ScenarioData['files'] = [];

    for (const filePath of allowlist) {
      const response = await fetch(`https://api.github.com/repos/${owner}/xable-playground/contents/${filePath}`, {
        headers: {
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { success: false, error: `Scenario file not found: ${filePath}. This scenario may not be available in this fork.` };
        }
        if (response.status === 403) {
          return { success: false, error: 'Access denied. The repository may be private or rate limited.' };
        }
        return { success: false, error: `Could not read file: ${filePath}. Please try again later.` };
      }

      const fileData = await response.json();

      // Generate hash from content (base64 encoded from GitHub API)
      const content = fileData.content;
      const hash = await simpleHash(content);

      // Generate short summary from filename
      const summary = filePath.split('/').pop() || filePath;

      files.push({
        path: filePath,
        hash,
        summary
      });
    }

    return {
      success: true,
      data: {
        repoUrl: `https://github.com/${owner}/xable-playground`,
        scenarioId: scenarioName,
        files,
        timestamp: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('[Scenario Files] Error:', error);
    return { success: false, error: 'Failed to read scenario files. Please try again.' };
  }
}

/**
 * Simple hash function for file content.
 * In production, use a proper crypto hash.
 */
async function simpleHash(content: string): Promise<string> {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Phase 3: Generate .xable preview
 *
 * Generates public-safe .xable/ directory structure with project manifest and summary.
 * Does not expose internal governance structures.
 */
export interface XablePreview {
  'xable.project.json': string;
  'xable.summary.md': string;
}

export async function generateXablePreview(
  owner: string,
  repoUrl: string
): Promise<{ success: boolean; preview?: XablePreview; error?: string }> {
  try {
    // Generate xable.project.json
    const projectJson = {
      projectName: 'xable-playground',
      repoUrl: repoUrl,
      visibility: 'public_safe',
      mode: 'read_only',
      capabilities: [
        'simulate_changes',
        'suggest_patches',
        'summarize_project_state'
      ],
      selectedScenarios: [
        'react-dashboard-null-crash',
        'api-validation-missing',
        'async-state-race',
        'full-stack-stabilization',
        'governed-workflow-simulation',
        'korros-governed-agent-challenge'
      ],
      currentDecisionState: 'korros',
      generatedAt: new Date().toISOString()
    };

    // Generate xable.summary.md
    const summaryMd = `# Xable Playground Preview

## Connected Fork
${repoUrl}

## Detected Scenarios
- React Dashboard Null Crash
- API Validation Missing
- Async State Race
- Full-Stack Stabilization
- Governed Workflow Simulation
- Korros Governed Agent Challenge

## Test State
Intentionally failing scenarios designed to demonstrate Xable's governance and simulation capabilities.

## Suggested Next Move
Select a scenario from the list above to see Xable propose a safe change before you review.

## Korros / Kairos State
**Current State: Korros**

Korros is the safety-first decision state where Xable:
- Simulates all changes before applying
- Blocks unsafe operations
- Requires explicit approval for deploys
- Provides clear reasoning for every decision

## Security Note
This preview is generated from your public playground fork. Xable has read-only access to the selected files and cannot commit, push, merge, or deploy to your repository.

## Generated
${new Date().toISOString()}
`;

    return {
      success: true,
      preview: {
        'xable.project.json': JSON.stringify(projectJson, null, 2),
        'xable.summary.md': summaryMd
      }
    };
  } catch (error) {
    console.error('[Xable Preview] Error:', error);
    return { success: false, error: 'Failed to generate .xable preview. Please try again.' };
  }
}
